/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import type { SnapListingParams } from '../types';
import {
  addDays,
  format,
  isBefore,
  isValid,
  parseISO,
  startOfDay,
} from 'date-fns';

type ParamValue = string | string[] | number | boolean | undefined | null;

export type SnapListingDates = {
  fromDateFlight: string;
  fromDateHotel: string;
  toDateFlight: string;
  toDateHotel: string;
};

const firstValue = (value: ParamValue): string => {
  if (Array.isArray(value)) {
    return firstValue(value[0]);
  }

  if (value === undefined || value === null) {
    return '';
  }

  return String(value).trim();
};

const LISTING_DATE_FORMAT = 'yyyy-MM-dd';

const parseListingDate = (value: string) => {
  if (!value) return null;

  const parsedDate = parseISO(value);
  return isValid(parsedDate) ? parsedDate : null;
};

const formatListingDate = (date: Date) => format(date, LISTING_DATE_FORMAT);

const parseOptionalNumber = (value: string) => {
  if (!value) return undefined;

  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : undefined;
};

const getListingTripType = (tripType: string, toDateFlight: string) => {
  const normalizedTripType = tripType.toUpperCase();

  if (
    normalizedTripType === 'O' ||
    normalizedTripType === 'ONEWAY' ||
    normalizedTripType === 'ONE_WAY' ||
    normalizedTripType === 'ONE-WAY'
  ) {
    return 'O';
  }

  if (
    toDateFlight &&
    (normalizedTripType === 'R' || normalizedTripType === 'ROUNDTRIP')
  ) {
    return 'R';
  }

  return toDateFlight ? 'R' : 'O';
};

export const getNormalizedSnapListingDates = ({
  fromDateFlight,
  fromDateHotel,
  toDateFlight,
  toDateHotel,
}: SnapListingDates): SnapListingDates => {
  const departDate = parseListingDate(fromDateFlight);
  if (!departDate) {
    return {
      fromDateFlight,
      fromDateHotel,
      toDateFlight,
      toDateHotel,
    };
  }

  const today = startOfDay(new Date());
  if (isBefore(departDate, today)) {
    const fallbackFromDate = addDays(today, 7);
    const fallbackToDate = addDays(fallbackFromDate, 2);

    return {
      fromDateFlight: formatListingDate(fallbackFromDate),
      fromDateHotel: formatListingDate(fallbackFromDate),
      toDateFlight: formatListingDate(fallbackToDate),
      toDateHotel: formatListingDate(fallbackToDate),
    };
  }

  return {
    fromDateFlight,
    fromDateHotel,
    toDateFlight,
    toDateHotel,
  };
};

export const hasMandatorySnapListingParams = (
  params: Record<string, ParamValue>,
) =>
  Boolean(
    firstValue(params.orig) &&
      firstValue(params.dest) &&
      firstValue(params.fromDateFlight) &&
      firstValue(params.rooms) &&
      firstValue(params.adults) &&
      firstValue(params.currency) &&
      firstValue(params.language),
  );

/**
 * Hotels-web style gate: wait until locale finished loading and matches the
 * listing language (when both are present) before firing search queries.
 */
export const isSnapListingLocaleReady = (
  isLocaleLoading: boolean,
  currentLanguage?: string,
  paramsLanguage?: string,
) =>
  !isLocaleLoading &&
  (!currentLanguage ||
    !paramsLanguage ||
    currentLanguage.toLowerCase() === paramsLanguage.toLowerCase());

export const getSnapListingParams = (
  params: Record<string, ParamValue>,
): SnapListingParams => {
  const fromDateFlight = firstValue(params.fromDateFlight);
  const toDateFlight = firstValue(params.toDateFlight);
  const fromDateHotel = firstValue(params.fromDateHotel) || fromDateFlight;
  const toDateHotel = firstValue(params.toDateHotel) || toDateFlight;
  const normalizedDates = getNormalizedSnapListingDates({
    fromDateFlight,
    fromDateHotel,
    toDateFlight,
    toDateHotel,
  });
  const hotelSize = parseOptionalNumber(
    firstValue(params.hotelSize || params.hotels_size),
  );

  return {
    tripType: normalizedDates.toDateFlight
      ? 'R'
      : getListingTripType(
          firstValue(params.tripType),
          normalizedDates.toDateFlight,
        ),
    orig: firstValue(params.orig),
    dest: firstValue(params.dest),
    fromDateFlight: normalizedDates.fromDateFlight,
    toDateFlight: normalizedDates.toDateFlight,
    fromDateHotel: normalizedDates.fromDateHotel,
    toDateHotel: normalizedDates.toDateHotel,
    rooms: firstValue(params.rooms),
    adults: firstValue(params.adults || params.guest_adults),
    children: firstValue(params.children),
    infants: firstValue(params.infants),
    country_code: firstValue(params.country_code || params.countryCode),
    currency: firstValue(params.currency),
    language: firstValue(params.language || params.languageId),
    snapId: firstValue(params.snapId),
    airlineProfile: firstValue(params.airlineProfile || params.airProfile),
    departFlightId: firstValue(params.departFlightId),
    returnFlightId: firstValue(params.returnFlightId),
    Usermailid: firstValue(
      params.Usermailid || params.userMailId || params.usermailid,
    ),
    tier: firstValue(params.tier),
    gaid: firstValue(params.gaid || params.GAID),
    ...(hotelSize !== undefined ? { hotelSize } : {}),
  };
};
